import React from 'react';
import { motion } from 'framer-motion';
/**
 * WordClassChip — Strip of Dutch word-class chips (woordsoorten).
 *
 * Pairs with the `word_class_tap` mode on text_highlight, and serves as a
 * legend/support visual for SOORT-* and ONT-* families.
 *
 * Audit reference: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026.md V1
 */
export type WordClass = 'lidwoord' | 'zelfstandig naamwoord' | 'bijvoeglijk naamwoord' | 'werkwoord' | 'bijwoord' | 'voornaamwoord' | 'voorzetsel' | 'voegwoord' | 'telwoord';
interface WordClassChipProps {
  highlightClass?: WordClass;
  showLegend?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
interface ChipDef {
  abbr: string;
  full: WordClass;
  /** Tailwind classes — colors mirror sentenceDiagram palette for consistency. */
  bg: string;
  text: string;
  ring: string;
}
const CHIPS: ChipDef[] = [{
  abbr: 'LW',
  full: 'lidwoord',
  bg: 'bg-slate-100',
  text: 'text-slate-700',
  ring: 'ring-slate-300'
}, {
  abbr: 'ZN',
  full: 'zelfstandig naamwoord',
  bg: 'bg-blue-100',
  text: 'text-blue-800',
  ring: 'ring-blue-400'
}, {
  abbr: 'BNW',
  full: 'bijvoeglijk naamwoord',
  bg: 'bg-emerald-100',
  text: 'text-emerald-800',
  ring: 'ring-emerald-400'
}, {
  abbr: 'WW',
  full: 'werkwoord',
  bg: 'bg-rose-100',
  text: 'text-rose-800',
  ring: 'ring-rose-400'
}, {
  abbr: 'BW',
  full: 'bijwoord',
  bg: 'bg-amber-100',
  text: 'text-amber-800',
  ring: 'ring-amber-400'
}, {
  abbr: 'VNW',
  full: 'voornaamwoord',
  bg: 'bg-violet-100',
  text: 'text-violet-800',
  ring: 'ring-violet-400'
}, {
  abbr: 'VZ',
  full: 'voorzetsel',
  bg: 'bg-cyan-100',
  text: 'text-cyan-800',
  ring: 'ring-cyan-400'
}, {
  abbr: 'VW',
  full: 'voegwoord',
  bg: 'bg-fuchsia-100',
  text: 'text-fuchsia-800',
  ring: 'ring-fuchsia-400'
}, {
  abbr: 'TW',
  full: 'telwoord',
  bg: 'bg-orange-100',
  text: 'text-orange-800',
  ring: 'ring-orange-400'
}];
export function WordClassChip({
  highlightClass,
  showLegend = true,
  label,
  mode = 'support'
}: WordClassChipProps) {
  const isStimulus = mode === 'stimulus';
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`bg-white rounded-xl border border-slate-200 ${isStimulus ? 'p-3' : 'p-4'}`} role="group" aria-label={label || 'Woordsoorten'} data-id="element-14298" data-mp="2kbodcjtkmnec">
      {label && <h4 className={`font-semibold text-slate-800 mb-2 ${isStimulus ? 'text-sm' : 'text-base'}`} data-id="element-14299" data-mp="21jc0v497e5qh">
          {label}
        </h4>}
      <div className="flex flex-wrap gap-1.5" data-id="element-14300" data-mp="18vpx9vzz713m">
        {CHIPS.map(chip => {
        const isActive = chip.full === highlightClass;
        return <div key={chip.abbr} className="flex flex-col items-center gap-1" data-id="element-14301" data-mp="1dsq4494ng7vs">
              <span title={chip.full} aria-label={chip.full} className={`inline-flex items-center justify-center rounded-md font-bold transition-all ${chip.bg} ${chip.text} ${isStimulus ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm'} ${isActive ? `ring-2 ring-offset-1 ${chip.ring} scale-105` : ''}`} data-id="element-14302" data-mp="169p8ea03biiz">
                {chip.abbr}
              </span>
              {!isStimulus && <span className={`text-[10px] ${isActive ? 'font-bold text-slate-700' : 'text-slate-400'}`} data-id="element-14303" data-mp="ilfrnmhqhkyp">
                  {chip.full}
                </span>}
            </div>;
      })}
      </div>
      {showLegend && highlightClass && <p className={`mt-2 text-slate-600 ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-14304" data-mp="4logdtilqioe">
          Gemarkeerd:{' '}
          <span className="font-semibold text-slate-800" data-id="element-14305" data-mp="3vrplllfvyi24">{highlightClass}</span>
        </p>}
    </motion.div>;
}